"use client"

import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface SearchPaginationProps {
  currentPage: number
  hasNextPage: boolean
  totalResults: number
  onPageChange: (page: number) => void
}

export function Pagination({ currentPage, hasNextPage, onPageChange }: SearchPaginationProps) {
  const maxVisiblePages = 5
  const hasPrevPage = currentPage > 1

  const getPageNumbers = () => {
    const pages: (number | string)[] = []

    if (currentPage <= 3) {
      for (let i = 1; i <= Math.min(maxVisiblePages, currentPage + 2); i++) {
        pages.push(i)
      }
      if (hasNextPage && currentPage + 2 < maxVisiblePages) {
        pages.push("...")
      }
    } else {
      pages.push(1)
      pages.push("...")
      pages.push(currentPage - 1)
      pages.push(currentPage)
      if (hasNextPage) {
        pages.push(currentPage + 1)
        pages.push("...")
      }
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className="flex flex-col items-center gap-4 border-t border-border pt-4">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevPage}
          className="h-9 w-9 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {pageNumbers.map((page, index) => {
          if (page === "...") {
            return (
              <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                ...
              </span>
            )
          }

          return (
            <Button
              key={page}
              variant={page === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(page as number)}
              className="h-9 w-9 p-0"
            >
              {page}
            </Button>
          )
        })}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className="h-9 w-9 p-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
